import fuzzysort from "fuzzysort";

export function useCustomContentSearch(
  options: {
    initialize: boolean;
  } = { initialize: false },
) {
  const _defaultPageSize = 18;

  const { locale, t } = useI18n();
  const { path, query } = useRoute();

  // below states need to be global states so every instance of this composable works with the same values
  const contentSearchResults = useContentSearchResults(path);
  const contentSearchDisplay = useContentSearchDisplay(path);
  const contentSearchPagination = useContentSearchPagination(path);
  const contentSearchSearchState = useContentSearchSearchState(path);

  const { status, error } = useFetch("/api/cms/search-content", {
    // server: false,
    method: "GET",
    lazy: true,
    immediate: options.initialize,
    dedupe: "defer",
    query: { locale: locale },
    watch: false,
    deep: false,
    onRequest: ({ request }) => {
      // eslint-disable-next-line no-console
      console.info(`[Content] Search request (${JSON.stringify(request)})`);
    },
    onResponse: ({ response }) => {
      contentSearchResults.value = response._data;
      search();
    },
  });

  const activeTitle = computed(() => {
    if (!contentSearchSearchState.value.term) {
      return "";
    }

    return `${t("catalog.search-by")} '${contentSearchSearchState.value.term}'`;
  });

  function search() {
    if (!query.term || query.term.length < 3) {
      return;
    }

    const searchOptions = {
      keys: ["title", "content"],
      threshold: 0.3, // don't return bad results
    };

    const searchResults = fuzzysort.go(query.term as string, contentSearchResults.value, searchOptions);
    const filteredSearchResults = searchResults.filter((r: any) => r.obj);

    if (!filteredSearchResults.length) {
      return;
    }

    const mappedSearchResults = filteredSearchResults
      .map((r: any) => ({
        key: r.obj.key,
        id: r.obj.id,
        title: r.obj.title,
        slug: r.obj.slug,
        content: r.obj.content,
        teaserText: r.obj.teaserText,
        teaserImage: r.obj.teaserImage,
      }));

    contentSearchPagination.value.pageSize = _defaultPageSize;
    contentSearchPagination.value.totalItems = mappedSearchResults.length;

    contentSearchSearchState.value.term = query.term as string;

    const startIndex = (contentSearchSearchState.value.page - 1) * _defaultPageSize;
    const endIndex = startIndex + _defaultPageSize;
    contentSearchDisplay.value = mappedSearchResults.slice(startIndex, endIndex) ?? [];
  }

  if (options.initialize) {
    watch(() => contentSearchSearchState.value.page, async (oldPage, newPage) => {
      if (oldPage !== newPage) {
        const overview = document?.querySelector("#scroll-into-view");
        overview?.scrollIntoView({ behavior: "smooth" });
      }

      search();
    });
  }

  return {
    status: status,
    error: error,
    content: contentSearchDisplay,
    searchState: contentSearchSearchState,
    activeTitle: activeTitle,
    pagination: contentSearchPagination,
  };
}
